import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft } from '@phosphor-icons/react';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { UserEditModal } from './UserEditModal';
import { ApplyCouponModal } from './ApplyCouponModal';
import { SAProfileSkeleton } from './SASkeletons';
import { toast } from '@/hooks/use-toast';
import { useUserAnalytics } from '@/hooks/useUserAnalytics';

const monoFont = "'IBM Plex Mono', monospace";

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  site_name: string;
  plan: string;
  status: string;
  notes: string | null;
  billing_email: string | null;
  last_access_at: string | null;
  created_at: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

function formatDate(d: string) { return new Date(d).toLocaleDateString('pt-BR'); }
function relativeTime(d: string | null) {
  if (!d) return 'nunca';
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'hoje';
  if (days === 1) return 'ontem';
  return `${days}d atrás`;
}
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
}

const statusColors: Record<string, string> = { active: '#00E87A', suspended: '#F0E614', cancelled: '#FF3F2D', pending: 'rgba(247,246,242,0.4)' };

export function SAUserProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [cmsUsage, setCmsUsage] = useState<any>(null);
  const [projectsCount, setProjectsCount] = useState(0);
  const [pvTotal, setPvTotal] = useState(0);
  const [activeCoupon, setActiveCoupon] = useState<any>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);

  const { data: analytics, loading: analyticsLoading } = useUserAnalytics(profile?.username || '');

  const loadProfile = async () => {
    if (!id) return;
    const { data: prof } = await supabase.from('user_profiles').select('*').eq('user_id', id).single();
    if (prof) setProfile(prof as any);

    const { data: usage } = await supabase.from('cms_usage').select('*').eq('user_id', id).maybeSingle();
    if (usage) setCmsUsage(usage);

    const { count: pc } = await supabase.from('projects').select('id', { count: 'exact', head: true }).eq('user_id', id);
    setProjectsCount(pc || 0);

    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
    const { count: pv } = await supabase.from('page_views').select('id', { count: 'exact', head: true }).eq('site_user_id', id).gte('created_at', thirtyDaysAgo);
    setPvTotal(pv || 0);

    // Active coupon
    const { data: uc } = await supabase.from('user_coupons').select('*, coupons(code, discount_type, discount_value)').eq('user_id', (prof as any)?.id).order('applied_at', { ascending: false }).limit(1).maybeSingle();
    if (uc) setActiveCoupon(uc);

    setLoading(false);
  };

  useEffect(() => { loadProfile(); }, [id]);

  const handleSuspend = async () => {
    if (!profile) return;
    const newStatus = profile.status === 'suspended' ? 'active' : 'suspended';
    await supabase.from('user_profiles').update({ status: newStatus }).eq('id', profile.id);
    setProfile({ ...profile, status: newStatus });
    setShowSuspend(false);
    toast({ title: newStatus === 'suspended' ? 'Usuário suspenso' : 'Usuário reativado' });
  };

  const handleDelete = async () => {
    if (!profile || deleteConfirm !== profile.site_name) return;
    setDeleteLoading(true);
    const res = await supabase.functions.invoke('delete-user', { body: { userId: profile.user_id } });
    if (res.error) {
      toast({ title: 'Erro ao deletar', variant: 'destructive' });
      setDeleteLoading(false);
      return;
    }
    toast({ title: 'Usuário deletado' });
    navigate('/admin/usuarios');
  };

  if (loading || !profile) return <SAProfileSkeleton />;

  const infoRows = [
    ['Último acesso CMS', relativeTime(profile.last_access_at)],
    ['Site publicado', cmsUsage?.last_published_at ? 'Sim' : 'Não'],
    ['Projetos', `${projectsCount}`],
    ['Storage', formatBytes(cmsUsage?.disk_usage_bytes || 0)],
    ['Total de imagens', `${cmsUsage?.total_images || 0}`],
  ];

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate('/admin/usuarios')}
        style={{
          fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)',
          background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          marginBottom: 20, padding: 0, transition: 'color 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#F7F6F2'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(247,246,242,0.4)'}
      >
        <ArrowLeft size={14} weight="thin" /> Usuários
      </button>

      {/* Identity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div style={{
          width: 50, height: 50, backgroundColor: 'rgba(247,246,242,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: monoFont, fontSize: 20, fontWeight: 300, color: 'rgba(247,246,242,0.5)',
          borderRadius: 12, flexShrink: 0,
        }}>
          {profile.username[0]?.toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: monoFont, fontSize: 18, fontWeight: 300, color: '#F7F6F2' }}>{profile.username}</div>
          <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.5)' }}>
            {profile.billing_email || '—'}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <span style={{ fontFamily: monoFont, fontSize: 12, padding: '2px 6px', border: `0.5px solid ${statusColors[profile.status]}`, color: statusColors[profile.status] }}>
              {profile.status.toUpperCase()}
            </span>
            <span style={{ fontFamily: monoFont, fontSize: 12, padding: '2px 6px', border: '0.5px solid rgba(247,246,242,0.15)', color: profile.plan === 'pro' ? '#F7F6F2' : 'rgba(247,246,242,0.4)' }}>
              {profile.plan.toUpperCase()}
            </span>
            <span style={{ fontFamily: monoFont, fontSize: 12, padding: '2px 6px', color: 'rgba(247,246,242,0.3)' }}>
              {formatDate(profile.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowSuspend(true)}
          style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 14px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent',
            color: profile.status === 'suspended' ? '#00E87A' : '#F0E614', cursor: 'pointer',
          }}
        >{profile.status === 'suspended' ? 'Reativar' : 'Suspender'}</button>
        <button
          onClick={() => setShowCoupon(true)}
          style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 14px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent',
            color: 'rgba(247,246,242,0.6)', cursor: 'pointer',
          }}
        >Aplicar cupom</button>
        <button
          onClick={() => setShowEdit(true)}
          style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 14px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent',
            color: 'rgba(247,246,242,0.6)', cursor: 'pointer',
          }}
        >Editar</button>
        <button
          onClick={() => setShowDelete(true)}
          style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 14px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent',
            color: '#FF3F2D', cursor: 'pointer',
          }}
        >Apagar</button>
      </div>

      {/* Platform section */}
      <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 12 }}>
        PLATAFORMA
      </div>
      <div style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', marginBottom: 24 }}>
        {infoRows.map(([label, value], i) => (
          <div key={label} style={{
            display: 'flex', justifyContent: 'space-between', padding: '12px 16px',
            borderBottom: i < infoRows.length - 1 ? '0.5px solid rgba(247,246,242,0.12)' : 'none',
          }}>
            <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)', textTransform: 'uppercase' }}>{label}</span>
            <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: 'rgba(247,246,242,0.8)' }}>{value}</span>
          </div>
        ))}
      </div>

      {/* Analytics */}
      <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 12 }}>
        ANALYTICS DO SITE PÚBLICO
      </div>
      <div style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', marginBottom: 24 }}>
        {analyticsLoading ? (
          <>
            <div style={{ padding: '12px 16px', borderBottom: '0.5px solid rgba(247,246,242,0.12)' }}>
              <div style={{ height: 14, width: '60%', backgroundColor: 'rgba(247,246,242,0.06)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
            <div style={{ padding: '12px 16px', borderBottom: '0.5px solid rgba(247,246,242,0.12)' }}>
              <div style={{ height: 14, width: '45%', backgroundColor: 'rgba(247,246,242,0.06)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
            <div style={{ padding: '12px 16px' }}>
              <div style={{ height: 14, width: '35%', backgroundColor: 'rgba(247,246,242,0.06)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '0.5px solid rgba(247,246,242,0.12)' }}>
              <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)', textTransform: 'uppercase' }}>VIEWS (30D)</span>
              <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: analytics?.pageviews ? '#F7F6F2' : 'rgba(247,246,242,0.3)' }}>
                {analytics?.pageviews ?? '—'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '0.5px solid rgba(247,246,242,0.12)' }}>
              <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)', textTransform: 'uppercase' }}>VISITANTES ÚNICOS</span>
              <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: analytics?.visitors ? '#F7F6F2' : 'rgba(247,246,242,0.3)' }}>
                {analytics?.visitors ?? '—'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px' }}>
              <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)', textTransform: 'uppercase' }}>PAÍS TOP</span>
              <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: analytics?.topCountry ? '#F7F6F2' : 'rgba(247,246,242,0.3)' }}>
                {analytics?.topCountry ?? '—'}
              </span>
            </div>
            {(!analytics?.pageviews && !analytics?.visitors) && (
              <div style={{ padding: '8px 16px 12px', fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.25)' }}>
                Site ainda não tem visitas registradas
              </div>
            )}
          </>
        )}
      </div>

      {/* Billing */}
      <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 12 }}>
        FATURAMENTO
      </div>
      <div style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '0.5px solid rgba(247,246,242,0.12)' }}>
          <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)' }}>PLANO ATUAL</span>
          <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: '#F7F6F2' }}>{profile.plan.toUpperCase()}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px' }}>
          <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)' }}>CUPOM ATIVO</span>
          <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: activeCoupon ? '#00E87A' : 'rgba(247,246,242,0.3)' }}>
            {activeCoupon ? `${(activeCoupon as any).coupons?.code || '—'}` : 'nenhum'}
          </span>
        </div>
      </div>

      {/* Modals */}
      <ConfirmDialog
        open={showSuspend}
        onOpenChange={setShowSuspend}
        title={profile.status === 'suspended' ? 'Reativar usuário' : 'Suspender usuário'}
        description={profile.status === 'suspended' ? `Reativar ${profile.site_name}?` : `Suspender ${profile.site_name}? O acesso ao CMS será bloqueado.`}
        confirmLabel={profile.status === 'suspended' ? 'Reativar' : 'Suspender'}
        cancelLabel="Cancelar"
        onConfirm={handleSuspend}
        destructive={profile.status !== 'suspended'}
      />

      {showDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setShowDelete(false)}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#0A0A0A', border: '0.5px solid rgba(247,246,242,0.12)', padding: 24, maxWidth: 400, width: '100%' }}>
            <div style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: '#F7F6F2', marginBottom: 12 }}>Apagar usuário</div>
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.6)', marginBottom: 16, lineHeight: 1.6 }}>
              Digite <strong style={{ color: '#FF3F2D' }}>{profile.site_name}</strong> para confirmar.
            </div>
            <input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder={profile.site_name}
              style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, width: '100%', padding: '8px 12px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'rgba(247,246,242,0.04)', color: '#F7F6F2', marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDelete(false)} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 16px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', cursor: 'pointer' }}>CANCELAR</button>
              <button onClick={handleDelete} disabled={deleteConfirm !== profile.site_name || deleteLoading}
                style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 16px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent', color: deleteConfirm === profile.site_name ? '#FF3F2D' : 'rgba(247,246,242,0.2)', cursor: deleteConfirm === profile.site_name ? 'pointer' : 'not-allowed' }}>
                {deleteLoading ? 'APAGANDO...' : 'APAGAR'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <UserEditModal user={profile} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); loadProfile(); }} />
      )}

      {showCoupon && (
        <ApplyCouponModal userId={profile.id} onClose={() => setShowCoupon(false)} onApplied={() => { setShowCoupon(false); loadProfile(); }} />
      )}
    </div>
  );
}
